import { Box } from "@mui/material";
import { CalendarArrowDown, CalendarSearch, CircleCheck } from "lucide-react";
import { Button, palette, Typography } from "@powerdiary/components-library/lib";
import { ROUTES } from "../../../../../routes";
import { useClientPortalNavigate } from "hooks/useClientPortalRouter";
import { IPaymentData } from "../../../bookingSlice.types";
import { useClientPortalConfigContext } from "context/ClientPortalConfig.ctx";

const wrapperSx = {
    width: "100%",
    gap: "24px",
    border: `1px solid ${palette.neutral["300"]}`,
    borderRadius: "4px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 24px",
    height: "fit-content",
};

interface ISuccessSectionProps {
    paymentData?: IPaymentData | null;
    appointmentId?: number | null;
}

const getSuccessMessage = (paymentData?: IPaymentData | null): string => {
    if (!paymentData?.hasPaid) {
        return "Your appointment is successfully booked";
    }

    if (paymentData.hasTotalAmount) {
        return "Full amount paid and your appointment is successfully booked";
    }

    return "Deposit paid and your appointment is successfully booked";
};

export default function SuccessSection({ paymentData, appointmentId }: Readonly<ISuccessSectionProps>) {
    const navigateTo = useClientPortalNavigate();
    const { clientPortalGuid } = useClientPortalConfigContext();
    const successMessage = getSuccessMessage(paymentData);

    const handleAddToCalendar = () => {
        if (!appointmentId || !clientPortalGuid) {
            return;
        }

        const url = `/api/v1/${clientPortalGuid}/Appointment/DownloadIcs?appointmentId=${appointmentId}`;
        globalThis.open(url, "_self");
    };

    return (
        <Box sx={wrapperSx} data-test-id="section_booking_successful">
            <Box display="flex" flexDirection="column" gap="8px" alignItems="center">
                <CircleCheck color={palette.success["500"]} size={24} strokeWidth={2} aria-hidden />
                <Typography variant="paragraphLargeBold" textAlign="center" data-test-id="text_success_message">
                    {successMessage}
                </Typography>
            </Box>
            <Box display="flex" gap="8px">
                {appointmentId && (
                    <Button
                        variant="outlined"
                        size="extraSmall"
                        startIcon={<CalendarArrowDown size={14} strokeWidth={2} aria-hidden />}
                        onClick={handleAddToCalendar}
                        data-test-id="button_add_to_calendar"
                    >
                        ADD TO CALENDAR
                    </Button>
                )}
                <Button
                    variant="outlined"
                    size="extraSmall"
                    startIcon={<CalendarSearch size={14} strokeWidth={2} aria-hidden />}
                    onClick={() => navigateTo(ROUTES.APPOINTMENTS.urlPath)}
                    data-test-id="button_view_bookings"
                >
                    VIEW BOOKINGS
                </Button>
            </Box>
        </Box>
    );
}
